// 🔓 Game gating (MOD_ARC_SPEC_2026_07_13.md §3 — table approved by Nina 07-13)
//
// Per-user progress lives in localStorage; milestones are bumped from the
// surfaces where they actually happen (chat submit, poll vote, /build, watch
// party seat, dossier unlock, space visits). Philosophy: SOFT gate — locked
// games stay visible with their unlock hint (desire engine, not a wall).
// Rules: the featured game of the week is FREE for everyone; 3 plays during
// its featured week keep it forever; Bot the Explorer is exempt from all of it.

const KEY = 'gameProgress.v1';

function read() {
  try { return JSON.parse(localStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
}
function write(p) {
  try { localStorage.setItem(KEY, JSON.stringify(p)); } catch (e) { /* private mode */ }
}

export function bumpProgress(field, n = 1) {
  const p = read();
  p[field] = (p[field] || 0) + n;
  write(p);
}

export function addVisitDay() {
  const p = read();
  const day = new Date().toISOString().slice(0, 10);
  const days = Array.isArray(p.visitDays) ? p.visitDays : [];
  if (!days.includes(day)) {
    p.visitDays = [...days, day].slice(-30);
    write(p);
  }
}

// 3 plays during a game's featured week = keep it forever.
export function addFeaturedPlay(templateIndex, weekKey) {
  const p = read();
  const plays = p.featuredPlays || {};
  const k = `${templateIndex}:${weekKey || 'w'}`;
  plays[k] = (plays[k] || 0) + 1;
  p.featuredPlays = plays;
  if (plays[k] >= 3) {
    const kept = new Set(p.keptGames || []);
    kept.add(templateIndex);
    p.keptGames = [...kept];
  }
  write(p);
}

// 🔒 FULL LOCKDOWN (Nina 08-13): only the featured game (pinned to
// 2 Sentence to Horror in featuredCompetitions.js) is playable; every other
// template renders locked with a mystery hint — the tier table below is
// bypassed. Bot the Explorer keeps its exemption. Flip to false to restore
// tier-based unlocks.
const LOCKDOWN_ALL_EXCEPT_FEATURED = true;
const LOCKDOWN_HINT = 'An exciting game waiting to be unlocked!';

// Tier table keyed by template NAME (indexes could shift). null = always
// free (tier 0). Unknown names ship unlocked — a new template must never
// launch dead. Hints are player-facing: short, punchy, no em dashes.
const TIERS = {
  '2 Sentence to Horror': null,
  'Hot Takes Arena': null,
  'AI Roast Battle': { hint: 'Send 3 messages in any space to unlock!', check: (p) => (p.msgs || 0) >= 3 },
  'Find the Golden Glitch': { hint: 'Uncover a DOSSIER fact to unlock!', check: (p) => (p.dossierFacts || 0) >= 1 },
  'Villain Takeover': { hint: 'Build something with /build to unlock!', check: (p) => (p.crafts || 0) >= 1 },
  'Worldbuilding Clash': { hint: 'Vote in a room poll to unlock!', check: (p) => (p.pollVotes || 0) >= 1 },
  'Plot Twist Masters': { hint: 'Join a watch party to unlock!', check: (p) => (p.watchParties || 0) >= 1 },
  'Whodunnit': { hint: 'Visit on 3 different days, or craft 3 things!', check: (p) => ((p.visitDays || []).length >= 3) || (p.crafts || 0) >= 3 },
  // Nina 07-15: Doodle Bug is an unlockable — you earn it by drawing. The
  // mod can also spin one up any time via /doodle (mod path bypasses gates).
  'Doodle Bug': { hint: 'Draw a doodle in any space to unlock!', check: (p) => (p.doodles || 0) >= 1 },
};

export function isGameUnlocked(template, featuredIndex, isBot = false) {
  if (isBot) return true; // Bot the Explorer gets them all
  if (template.index === featuredIndex) return true; // ⭐ free this week
  if (LOCKDOWN_ALL_EXCEPT_FEATURED) return false;
  const tier = TIERS[template.name];
  if (tier === undefined || tier === null) return true;
  const p = read();
  if ((p.keptGames || []).includes(template.index)) return true;
  return tier.check(p);
}

// True when the template is gated AT ALL (drives the FREE THIS WEEK badge:
// tier-0 games featured week is nothing special, a gated one is a headline).
export function isGameGated(template) {
  // Lockdown note: stays tier-based on purpose — this only drives the hero
  // card's FREE THIS WEEK badge, and the featured game (tier-0 2SH) earning
  // a badge would read as a lie.
  const tier = TIERS[template.name];
  return tier !== undefined && tier !== null;
}

export function unlockHint(template) {
  if (LOCKDOWN_ALL_EXCEPT_FEATURED) return LOCKDOWN_HINT;
  return (TIERS[template.name] && TIERS[template.name].hint) || '';
}

// ── 🔑 Progress sync (07-14) ─────────────────────────────────────────────────
// Server mirror of this store, keyed by an unguessable recovery code (bearer
// semantics; minted once per browser). A cleared browser types the code back
// in and gets its unlocks back. Server merge is monotone, so applying the
// response can only ever ADD progress.

const CODE_KEY = 'gameProgressCode.v1';
const CODE_WORDS_A = ['amber', 'brave', 'clever', 'dusty', 'echo', 'foggy', 'golden', 'hazel', 'ivory', 'jolly', 'lunar', 'mellow', 'noble', 'opal', 'plucky', 'quiet', 'rusty', 'silver', 'tidal', 'velvet'];
const CODE_WORDS_B = ['otter', 'falcon', 'badger', 'comet', 'dragon', 'ember', 'fox', 'griffin', 'heron', 'ibis', 'jackal', 'koala', 'lynx', 'moth', 'newt', 'orca', 'panda', 'raven', 'sparrow', 'tiger'];

export function getSyncCode() {
  try {
    let code = localStorage.getItem(CODE_KEY);
    if (!code) {
      const hex = [...crypto.getRandomValues(new Uint8Array(3))].map(b => b.toString(16).padStart(2, '0')).join('');
      code = `${CODE_WORDS_A[Math.floor(Math.random() * CODE_WORDS_A.length)]}-${CODE_WORDS_B[Math.floor(Math.random() * CODE_WORDS_B.length)]}-${hex}`;
      localStorage.setItem(CODE_KEY, code);
    }
    return code;
  } catch (e) { return null; /* private mode: no sync */ }
}

// Adopt a typed-in code (restore path): future syncs write to ITS row.
export function setSyncCode(code) {
  try { localStorage.setItem(CODE_KEY, code); } catch (e) { /* private mode */ }
}

export function getProgressSnapshot() {
  return read();
}

// Merge the server's (monotone-merged) progress into localStorage. Numbers
// win by max, sets union — mirrors the server so the two never fight.
export function applyServerProgress(server) {
  if (!server || typeof server !== 'object') return;
  const p = read();
  for (const f of ['msgs', 'crafts', 'pollVotes', 'watchParties', 'dossierFacts']) {
    p[f] = Math.max(p[f] || 0, Number(server[f]) || 0);
    if (!p[f]) delete p[f];
  }
  p.visitDays = [...new Set([...(p.visitDays || []), ...(server.visitDays || [])])].sort().slice(-30);
  // keptGames are numeric template indexes — normalize whatever came back
  p.keptGames = [...new Set([...(p.keptGames || []), ...(server.keptGames || []).map(Number).filter(n => Number.isFinite(n))])];
  p.celebrated = [...new Set([...(p.celebrated || []), ...(server.celebrated || []).map(String)])];
  const plays = { ...(server.featuredPlays || {}) };
  for (const [k, v] of Object.entries(p.featuredPlays || {})) {
    plays[k] = Math.max(Number(plays[k]) || 0, v || 0);
  }
  if (Object.keys(plays).length) p.featuredPlays = plays;
  write(p);
}

// Celebration bookkeeping: which gated templates crossed into unlocked since
// we last looked. Marks them celebrated and returns them (usually 0 or 1).
export function takeNewUnlocks(templates) {
  if (LOCKDOWN_ALL_EXCEPT_FEATURED) return []; // no unlock celebrations while everything is sealed
  const p = read();
  const done = new Set(p.celebrated || []);
  const fresh = (templates || []).filter((t) => {
    const tier = TIERS[t.name];
    if (!tier || done.has(t.name)) return false;
    if ((p.keptGames || []).includes(t.index)) return true;
    return tier.check(p);
  });
  if (fresh.length) {
    p.celebrated = [...done, ...fresh.map((t) => t.name)];
    write(p);
  }
  return fresh;
}
